import React, {Component} from 'react';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import axios from 'axios';
import moment from 'moment';
import {Modal} from 'react-bootstrap';

import LoadingSpinner from 'js/components/loading_spinner';
import BrandCampaignSelect from 'js/publisher_leads/brand_campaign_select';

export class FilterSelect extends Component {
    static propTypes = {
        filtersApiUrl: PropTypes.string.isRequired,
        handleFilterSelect: PropTypes.func.isRequired
    };

    constructor(props) {
        super(props);
        this.state = {
            transmitting: false,
            filters: [],
            modalShown: false
        };
    }

    shouldComponentUpdate(nextProps, nextState) {
        return !R.equals(nextState, this.state);
    }

    insertableFilter = R.pipe(
        R.omit([
            'created_at',
            'crm_filter_id',
            'description',
            'id',
            'paused',
            'position',
            'updated_at',
            'campaign_id'
        ]),

        // since R.filter({}) is useless in ramda 0.15.1, we reject nulls the hard way
        (filters) => {
            const filteredFilters = {};
            const isNotNil = R.complement(R.isNil);

            R.pipe(
                R.keys,
                R.forEach((key) => {
                    if (isNotNil(filters[key])) {
                        filteredFilters[key] = filters[key];
                    }
                })
            )(filters);

            return filteredFilters;
        },

        // this basically duplicates what's happening in
        // CampaignFiltersController@appendFilterTransformationsForFrontend.
        R.evolve({
            include_key_value_tags_v2: JSON.parse,
            exclude_key_value_tags_v2: JSON.parse,
            include_content_analysis_tags : JSON.parse,
            exclude_content_analysis_tags : JSON.parse,
            demographics_publisher_types_confidence: (f) => { if (f) return f * 100; },
            demographics_include_interests_confidence: (f) => { if (f) return f * 100; },
            boost_creators_for_brand_id: R.toString,
            registered: (f) => { // rTodo: remove this weird frontend enum from AdvancedFilters and create a php enum
                if (f) {
                    if (f === '0') return 'yes';
                    if (f === '1') return 'no';
                }
            }
        }),

        // momentify last_imported_at fields, transform age_range & demographics_age_range fields
        (filters) => {
            let builtFilters = filters;

            if (builtFilters.last_imported_at_start) {
                builtFilters = R.assoc(
                    'last_imported_start_moment',
                    moment(builtFilters.last_imported_at_start, 'YYYY-MM-DD'),
                    builtFilters
                );
            }

            if (builtFilters.last_imported_at_end) {
                builtFilters = R.assoc(
                    'last_imported_end_moment',
                    moment(builtFilters.last_imported_at_end, 'YYYY-MM-DD'),
                    builtFilters
                );
            }

            if (builtFilters.age_range) {
                builtFilters = R.assoc(
                    'age_range_min',
                    R.head(R.split('-', builtFilters.age_range)),
                    builtFilters
                );
                builtFilters = R.assoc(
                    'age_range_max',
                    R.last(R.split('-', builtFilters.age_range)),
                    builtFilters
                );
            }

            if (builtFilters.demographics_age_range) {
                builtFilters = R.assoc(
                    'demographics_age_range_min',
                    R.head(R.split('-', builtFilters.demographics_age_range)),
                    builtFilters
                );
                builtFilters = R.assoc(
                    'demographics_age_range_max',
                    R.last(R.split('-', builtFilters.demographics_age_range)),
                    builtFilters
                );
            }

            return builtFilters;
        }
    );

    toggleModalShown = callback => {
        this.setState({modalShown: !this.state.modalShown}, callback);
    };

    fetchFilters = params => {
        this.setState(
            {filters: [], transmitting: true},
            () => axios.get(this.props.filtersApiUrl, {params})
                .then(({data}) => {
                    this.setState(
                        {filters: data || [], transmitting: false},
                        () => this.toggleModalShown()
                    );
                })
                .catch(() => this.setState({transmitting: false}))
            );
    };

    handleFilterSelect = R.curry((filter, e) => {
        this.toggleModalShown(() => {
            this.props.handleFilterSelect(this.insertableFilter(filter));
        });
    });

    renderSpinner = () => {
        if (this.state.transmitting) {
            return <LoadingSpinner />;
        }
    };

    renderFilterListGroup = () => {
        const {filters} = this.state;

        const listItems = R.map(filter => (
            <a key={`filter-${filter.id}`} href="#select-campaign-filter" onClick={this.handleFilterSelect(filter)}>
                <pre>{JSON.stringify(this.insertableFilter(filter), null, 2)}</pre>
            </a>
        ))(filters);

        const displayed = R.isEmpty(listItems)
            ? <li className="list-group-item list-group-item-warning">No filters to select</li>
            : listItems;

        return (
            <div className="list-group">
                {displayed}
            </div>
        );
    };

    renderFilterSelectModal = () => {
        const {modalShown} = this.state;
        return (
            <div>
                <Modal show={modalShown} onHide={this.toggleModalShown}>
                    <Modal.Header>Select A Filter</Modal.Header>
                    <div id="select-crm-filters-modal-body">
                        <Modal.Body>{this.renderFilterListGroup()}</Modal.Body>
                    </div>
                </Modal>
            </div>
        );
    };

    render() {
        return (
            <div id="filter-select" className="row">
                <div className="row">
                    <div className="text-center">
                        {this.renderSpinner()}
                    </div>
                    {this.renderFilterSelectModal()}
                </div>
            </div>
        );
    }
}

export class AutomatedFilterSelect extends FilterSelect {
    static propTypes = R.merge(FilterSelect.propTypes, {
        campaignId: PropTypes.number.isRequired
    });

    render() {
        const {campaignId} = this.props;
        return (
            <div id="campaign-filter-select" className="row">
                <div className="row">
                    <button
                        onClick={() => this.fetchFilters({campaign_id: campaignId})}
                        className="btn automated-filters-button"
                    >
                        Use Automated Filter Suggestions
                    </button>
                    <div className="text-center">
                        {this.renderSpinner()}
                    </div>
                    {this.renderFilterSelectModal()}
                </div>
            </div>
        );
    }
}

export class CampaignFilterSelect extends FilterSelect {
    static propTypes = R.merge(FilterSelect.propTypes, {
        campaignFuzzySearchUrl: PropTypes.string.isRequired
    });

    constructor(props) {
        super(props);

        this.state = R.merge(this.state, {
            campaign_id: ''
        });
    }

    handleCampaignSelect = (campaignId) => {
        this.fetchFilters({campaign_id: campaignId});
    };

    render() {
        const {campaignFuzzySearchUrl} = this.props;
        return (
            <div id="campaign-filter-select" className="row">
                <div className="row">
                    <BrandCampaignSelect
                        campaignFuzzySearchUrl={campaignFuzzySearchUrl}
                        handleCampaignSelect={this.handleCampaignSelect}
                        showSubmitButton={false}
                    />
                    <div className="text-center">
                        {this.renderSpinner()}
                    </div>
                    {this.renderFilterSelectModal()}
                </div>
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./publisher_leads/campaign_filter_select.js